/*
    Used tutorial: https://medium.com/tinyso/how-to-create-a-modal-component-in-react-from-basic-to-advanced-a3357a2a716a
*/

import React, { useEffect, useState, useRef } from "react";

// styles
import "./SideViewModal.css";

// components
import ActionButton from "../../buttons/ActionButton/ActionButton.component";

// mui
import { Grid, Alert, AlertTitle, Stack, CircularProgress } from "@mui/material";

export default function SideViewModal({
  prefillData,
  show,
  onClose,
  onSubmit,
  variants = [],
  submitText,
  title = "No Title Set",
  errors = {},
  submitError,
  mutation = null,
  successObjectKey = null,
  entityName = "Item",
  operationType = "create",
  mutationVariables = null,
  refetchQueries = [],
  onSuccess = null,
}) {
  const [stage, setStage] = useState(0);
  const [variant, setVariant] = useState(variants.length > 1 ? 0 : 1);
  const [loading, setLoading] = useState(false);
  const currentForm = useRef();
  let footerActions = [];
  let form = null;

  // ✅ MOVED TO TOP: Helper for loading text based on operation type
  const getLoadingText = () => {
    switch (operationType) {
      case "create": return "Creating...";
      case "update": return "Updating...";
      case "delete": return "Deleting...";
      default: return "Submitting...";
    }
  };

  // ✅ MOVED TO TOP: Helper function for operation verbs
  const getOperationVerb = (type) => {
    switch (type) {
      case "create": return "created";
      case "update": return "updated";
      case "delete": return "deleted";
      default: return "created";
    }
  };

  // Notification function using window.history
  const showNotification = (message, type = "success") => {
    const notification = {
      type: type,
      text: message,
      timestamp: Date.now(),
    };
    const currentState = window.history.state?.state || {};
    window.history.replaceState(
      { ...currentState, notification: notification },
      ''
    );
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // Enhanced handleSubmit with notification support
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentForm.current && currentForm.current.getData) {
      const finalData = currentForm.current.getData();
      
      if (mutation) {
        setLoading(true);
        try {
          const result = await mutation({
            variables: mutationVariables || finalData,
            refetchQueries: refetchQueries,
          });

          const response = result.data?.[successObjectKey];
          
          const isSuccess = 
            response?.didCreate || 
            response?.didUpdate || 
            response?.didDelete || 
            response?.equipment || 
            response?.crewMember || 
            response?.job;

          if (isSuccess) {
            const successMessage = response?.message || 
              `${entityName} ${getOperationVerb(operationType)} successfully`;
            
            showNotification(successMessage, "success");
            setLoading(false);
            
            if (onSuccess) {
              onSuccess(result);
            }
            
            onClose();
          } else {
            const errorMessage = response?.message || 
              `Failed to ${getOperationVerb(operationType)} ${entityName}`;
            
            showNotification(errorMessage, "error");
            setLoading(false);
          }
        } catch (error) {
          const errorMessage = error.message || 
            `Error ${getOperationVerb(operationType)} ${entityName}`;
          
          showNotification(errorMessage, "error");
          setLoading(false);
        }
      } else {
        onSubmit(variants.length > 1 ? { variant, finalData } : finalData);
      }
    } else {
      console.error("ERROR: Form does not have the forwardRef set.");
    }
  };

  const closeOnEscapeKeyDown = (e) => {
    if ((e.charCode || e.keyCode) === 27) {
      onClose();
    }
  };

  useEffect(() => {
    document.body.addEventListener("keydown", closeOnEscapeKeyDown);
    return function cleanup() {
      document.body.removeEventListener("keydown", closeOnEscapeKeyDown);
    };
  }, []);

  if (variant !== 0) {
    if (variants.length > 0 && variants[variant - 1].component) {
      form = React.cloneElement(variants[variant - 1].component, {
        stage: stage,
        ref: currentForm,
        prefillData: prefillData,
        errors: errors,
      });
    }

    if (variants.length == 1) {
      if (variants[variant - 1].stages == 1) {
        footerActions.push(
          <ActionButton
            label={loading ? getLoadingText() : submitText}
            color="success"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          />
        );
      } else {
        if (stage == 0) {
          footerActions.push(
            <ActionButton
              label="Next"
              color="primary"
              onClick={() => setStage(stage + 1)}
              disabled={loading}
            />
          );
        } else if (stage < variants[variant - 1].stages - 1) {
          footerActions.push(
            <ActionButton
              label="Back"
              color="secondary"
              onClick={() => setStage(stage - 1)}
              disabled={loading}
            />
          );
          footerActions.push(
            <ActionButton
              label="Next"
              color="primary"
              onClick={() => setStage(stage + 1)}
              disabled={loading}
            />
          );
        } else if (stage == variants[variant - 1].stages - 1) {
          footerActions.push(
            <ActionButton
              label="Back"
              color="secondary"
              onClick={() => setStage(stage - 1)}
              disabled={loading}
            />
          );
          footerActions.push(
            <ActionButton
              label={loading ? getLoadingText() : submitText}
              color="success"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            />
          );
        }
      }
    } else if (
      variants.length > 1 &&
      stage < variants[variant - 1].stages - 1
    ) {
      footerActions.push(
        <ActionButton
          label="Back"
          color="secondary"
          onClick={() => {
            if (stage == 0) {
              setVariant(0);
              setStage(0);
            } else {
              setStage(stage - 1);
            }
          }}
          disabled={loading}
        />
      );
      footerActions.push(
        <ActionButton
          label="Next"
          color="primary"
          onClick={() => setStage(stage + 1)}
          disabled={loading}
        />
      );
    } else if (
      variants.length > 1 &&
      stage == variants[variant - 1].stages - 1
    ) {
      footerActions.push(
        <ActionButton
          label="Back"
          color="secondary"
          onClick={() => {
            if (stage == 0) {
              setVariant(0);
              setStage(0);
            } else {
              setStage(stage - 1);
            }
          }}
          disabled={loading}
        />
      );
      footerActions.push(
        <ActionButton
          label={loading ? getLoadingText() : submitText}
          color="success"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        />
      );
    }
  }

  return (
    <div
      className={`side-view-modal ${show ? "show-modal" : ""}`}
      onClick={onClose}
    >
      <div
        className="side-view-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="side-view-modal-header">
          <h2>{`${title} ${
            variants.length > 1 && variant !== 0
              ? `- ${variants[variant - 1].name}`
              : ""
          }`}</h2>
        </div>

        {submitError && (
          <Alert severity="error">
            <AlertTitle>An error has occurred.</AlertTitle>
            <p>{submitError}</p>
          </Alert>
        )}

        <div className="side-view-modal-body">
          {variants.length == 0 ? null : variant == 0 ? (
            <Stack spacing={2}>
              {variants.map((variantObject, index) => {
                return (
                  <ActionButton
                    key={index}
                    label={variantObject.name}
                    color="secondary"
                    onClick={() => setVariant(index + 1)}
                    disabled={loading}
                  />
                );
              })}
            </Stack>
          ) : null}
          {form}
        </div>
        <div className="side-view-modal-footer">
          <Grid container spacing={2}>
            {footerActions.map((button, index) => {
              return <Grid item key={index}>{button}</Grid>;
            })}
          </Grid>
        </div>
      </div>
    </div>
  );
}
